import * as Tone from 'tone';

export const PERCUSSION_CHANNEL = 9;

/* prettier-ignore */
export const GM_INSTRUMENTS: string[] = [
  'acoustic_grand_piano','bright_acoustic_piano','electric_grand_piano','honkytonk_piano',
  'electric_piano_1','electric_piano_2','harpsichord','clavinet',
  'celesta','glockenspiel','music_box','vibraphone',
  'marimba','xylophone','tubular_bells','dulcimer',
  'drawbar_organ','percussive_organ','rock_organ','church_organ',
  'reed_organ','accordion','harmonica','tango_accordion',
  'acoustic_guitar_nylon','acoustic_guitar_steel','electric_guitar_jazz','electric_guitar_clean',
  'electric_guitar_muted','overdriven_guitar','distortion_guitar','guitar_harmonics',
  'acoustic_bass','electric_bass_finger','electric_bass_pick','fretless_bass',
  'slap_bass_1','slap_bass_2','synth_bass_1','synth_bass_2',
  'violin','viola','cello','contrabass',
  'tremolo_strings','pizzicato_strings','orchestral_harp','timpani',
  'string_ensemble_1','string_ensemble_2','synth_strings_1','synth_strings_2',
  'choir_aahs','voice_oohs','synth_choir','orchestra_hit',
  'trumpet','trombone','tuba','muted_trumpet',
  'french_horn','brass_section','synth_brass_1','synth_brass_2',
  'soprano_sax','alto_sax','tenor_sax','baritone_sax',
  'oboe','english_horn','bassoon','clarinet',
  'piccolo','flute','recorder','pan_flute',
  'blown_bottle','shakuhachi','whistle','ocarina',
  'lead_1_square','lead_2_sawtooth','lead_3_calliope','lead_4_chiff',
  'lead_5_charang','lead_6_voice','lead_7_fifths','lead_8_bass_lead',
  'pad_1_new_age','pad_2_warm','pad_3_polysynth','pad_4_choir',
  'pad_5_bowed','pad_6_metallic','pad_7_halo','pad_8_sweep',
  'fx_1_rain','fx_2_soundtrack','fx_3_crystal','fx_4_atmosphere',
  'fx_5_brightness','fx_6_goblins','fx_7_echoes','fx_8_scifi',
  'sitar','banjo','shamisen','koto',
  'kalimba','bagpipe','fiddle','shanai',
  'tinkle_bell','agogo','steel_drums','woodblock',
  'taiko_drum','melodic_tom','synth_drum','reverse_cymbal',
  'guitar_fret_noise','breath_noise','seashore','bird_tweet',
  'telephone_ring','helicopter','applause','gunshot',
];

export function getGmInstrumentName(programNumber: number): string {
  return GM_INSTRUMENTS[programNumber] || 'acoustic_grand_piano';
}

const GM_PERCUSSION_TO_GROUP: Record<number, string> = {
  35: 'kick',
  36: 'kick',
  37: 'rimshot',
  38: 'snare',
  39: 'clap',
  40: 'snare',
  41: 'tom-low',
  42: 'hihat-close',
  43: 'tom-low',
  44: 'hihat-close',
  45: 'tom-mid',
  46: 'hihat-open',
  47: 'tom-mid',
  48: 'tom-hi',
  49: 'cymbal',
  50: 'tom-hi',
  51: 'cymbal',
  52: 'cymbal',
  53: 'cymbal',
  54: 'maracas',
  55: 'cymbal',
  56: 'cowbell',
  57: 'cymbal',
  58: 'cowbell',
  59: 'cymbal',
  60: 'conga-hi',
  61: 'conga-hi',
  62: 'conga-hi',
  63: 'conga-low',
  64: 'conga-low',
  69: 'maracas',
  70: 'maracas',
  75: 'clave',
  76: 'clave',
};

export function getPercussionGroupName(midiNote: number): string {
  return GM_PERCUSSION_TO_GROUP[midiNote] || 'rimshot';
}

export interface NotePlayer {
  start(note: number, time: number, duration: number, velocity: number): void;
  stop(): void;
  dispose(): void;
}

export interface DrumPlayer {
  hit(midiNote: number, time: number, velocity: number): void;
  stop(): void;
  dispose(): void;
}

const KICK_NOTES = new Set([35, 36]);
const SNARE_NOTES = new Set([38, 39, 40]);
const HIHAT_NOTES = new Set([42, 44, 46]);

export function createFallbackSynth(): NotePlayer {
  const synth = new Tone.PolySynth(Tone.Synth, {
    envelope: { attack: 0.02, decay: 0.1, sustain: 0.3, release: 0.8 },
  }).toDestination();
  synth.maxPolyphony = 64;
  synth.volume.value = -6;

  return {
    start: (note, time, duration, velocity) => {
      const freq = 440 * 2 ** ((note - 69) / 12);
      synth.triggerAttackRelease(freq, duration, time, velocity / 127);
    },
    stop: () => synth.releaseAll(),
    dispose: () => synth.dispose(),
  };
}

export function createFallbackDrums(): DrumPlayer {
  const kick = new Tone.MembraneSynth({
    pitchDecay: 0.05,
    octaves: 6,
    envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 0.4 },
  }).toDestination();
  const snare = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 0.001, decay: 0.15, sustain: 0, release: 0.1 },
  }).toDestination();
  const hihat = new Tone.MetalSynth({
    envelope: { attack: 0.001, decay: 0.08, release: 0.01 },
    harmonicity: 5.1,
    modulationIndex: 32,
    resonance: 4000,
    octaves: 1.5,
  }).toDestination();

  return {
    hit: (midiNote, time, velocity) => {
      const vel = velocity / 127;
      if (KICK_NOTES.has(midiNote)) {
        kick.triggerAttackRelease('C1', '8n', time, vel);
      } else if (SNARE_NOTES.has(midiNote)) {
        snare.triggerAttackRelease('8n', time, vel);
      } else if (HIHAT_NOTES.has(midiNote)) {
        hihat.triggerAttackRelease('32n', time, vel * 0.5);
      } else {
        hihat.triggerAttackRelease('32n', time, vel * 0.3);
      }
    },
    stop: () => {
      kick.triggerRelease();
      hihat.triggerRelease();
    },
    dispose: () => {
      kick.dispose();
      snare.dispose();
      hihat.dispose();
    },
  };
}
